import React, { useMemo } from "react";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Typography,
  Checkbox,
  InputAdornment,
  IconButton,
  Button,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import DialogTransition from "@icarius-common/dialogTransition";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { useDispatch, useSelector } from "react-redux";
import {
  MuiPickersUtilsProvider,
  DatePicker,
  TimePicker,
  KeyboardDatePicker,
  KeyboardTimePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { dateLocale } from "@icarius-localization/strings";
import { Autocomplete } from "@material-ui/lab";
import { GetAppIcon, LinkIcon, ImageIcon, DeleteIcon, VisibilityIcon, RoomIcon } from "@icarius-icons/index";
import { downloadFromURL, previewFromURL } from "@icarius-utils/download";
import { makeFile } from "@icarius-utils/fileUpload";
import Loader from "@icarius-common/loader";
import { RESOURCES_ENDPOINT } from "@icarius-connection/endpoints";
import { openDialogAction } from "@icarius-common/dialog/actions";
import { uploadSystemTableFormFileAction } from "../../../actions";
import { getIsLoadingFileUpload, getRestrictions } from "../../../selectors";
import { isRecordInBlockedList, getRecordProtectedFields, isFieldInProtectedList } from "../../../restrictionsUtils";
import { componentTypes } from "../../../constants";
import useHandleForm from "./useHandleForm";
import TextFieldMultiline from "@icarius-common/textFieldMultiline";
import CustomNumberFormat from '@icarius-common/dynamicInputs/customNumberFormat';

const gridStyle = { padding: "0px 10px" };
const flexStyle = { paddingTop: 12, display: "flex", alignItems: "center", cursor: "pointer" };

const acceptedFormatsFile = [
  'jpg',
  'bmp',
  'png',
  'jpeg',
  'rar',
  'zip',
  'gz',
  'tar',
  'xls',
  'xlsx',
  'txt',
  'rtf',
  'doc',
  'docx',
  'pdf',
  'html',
  'ppt',
  'pptx',
];

const acceptedFormatsImage = [
  'jpg',
  'bmp',
  'png',
  'jpeg',
];

const HelperTextComponent = ({ children }) => {
  return (
    <Grid container item xs={12} style={{ minHeight: 20 }}>
      <Typography className="whiteText" variant="caption">
        {children}
      </Typography>
    </Grid>
  )
}

const CreateEditDialog = (props) => {

  const {
    open,
    tableData,
    fields,
    level,
    isLoading,
    data,
    handleClose,
    create,
    modify,
    changeCheck,
    isSystemTables,
  } = props;

  const dispatch = useDispatch();
  const isLoadingFileUpload = useSelector(getIsLoadingFileUpload);
  const restrictionsFromStore = useSelector(getRestrictions);
  const restrictions = props.restrictions || restrictionsFromStore;
  const blockedRecords = restrictions?.blockedRecords;
  const protectedFields = restrictions?.protectedFields;

  const openValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
  }

  const {
    isCreate,
    formData,
    setFormValue,
    submit,
  } = useHandleForm(data, fields, create, modify, openValidationError);

  const isBlockedRecord = useMemo(() => {
    if (isCreate || !data) return false;
    return isRecordInBlockedList(data, blockedRecords);
  }, [isCreate, data, blockedRecords]);

  const recordProtectedFields = useMemo(() => {
    if (isCreate || !data) return [];
    return getRecordProtectedFields(data, protectedFields);
  }, [isCreate, data, protectedFields]);

  const isFieldDisabled = (field) => {
    const isFieldProtectedBySql = !isCreate && isFieldInProtectedList(field, recordProtectedFields);
    if (isBlockedRecord) return true;
    if (isFieldProtectedBySql) return true;
    if (tableData.editorActive === 'V') return true;
    if (tableData.withCheck && tableData.editorActive === 'M' && (level === 'M' || level === 'E')) return true;
    if (field.disabled === "N") return false;
    if (field.disabled === "E" && isCreate) return false;
    return true;
  };

  const visibleFields = Object.values(fields || {}).filter((field) => field && !field.hidden);
  const allFieldsDisabled = visibleFields.length > 0 && visibleFields.every(isFieldDisabled);

  const handleFileImageUpload = (e, fieldCode) => {
    makeFile(e.target.files[0], (myBase64) => {
      const dataToSend = {
        tableCode: tableData.code,
        codver: tableData.codver,
        code: fieldCode,
        file: myBase64,
      }

      dispatch(uploadSystemTableFormFileAction(isSystemTables, dataToSend))
        .then((resp) => {
          if (resp.status === 200) {
            setFormValue(resp.data.path, fieldCode);
          }
        })
    })
  }

  const getDialogActions = () => {
    const dataToRejectApprove = {
      internalCode: data?.internalCode,
      code: tableData.code,
      codver: tableData.codver,
    }

    const cancelButton = <ButtonDialogAction onClick={handleClose} text={getLocalizedString("disagree")} />;
    const acceptCloseButton = <ButtonDialogAction onClick={handleClose} text={getLocalizedString("agree")} />;
    const rejectButton = <ButtonDialogAction onClick={() => changeCheck(dataToRejectApprove, 'R')} text={getLocalizedString("rejectRequest")} />;
    const approveButton = <ButtonDialogAction isAccept onClick={() => changeCheck(dataToRejectApprove, 'A')} text={getLocalizedString("approveRequest")} />;

    const cancelAcceptButtons = (
      <>
        {cancelButton}
        <ButtonDialogAction onClick={submit} isAccept text={getLocalizedString("agree")} />
      </>
    );

    if (tableData.editorActive === "V") return acceptCloseButton;
    if (isBlockedRecord) return acceptCloseButton;
    if (isCreate || !tableData.withCheck) {
      return allFieldsDisabled ? acceptCloseButton : cancelAcceptButtons;
    }

    if (tableData.editorActive === "Y") {
      if (level === 'C' || level === 'M') {
        if (data.vis === "I") return cancelAcceptButtons;
        return cancelButton;
      }

      // si es empleador
      return cancelAcceptButtons;
    }

    if (tableData.editorActive === "M") {
      if (level === 'M' || level === 'E') {
        switch (data.vis) {
          case 'I': return (
            <>
              {cancelButton}
              {rejectButton}
              {approveButton}
            </>
          );
          case 'R': return (
            <>
              {cancelButton}
              {approveButton}
            </>
          );
          case 'A': return (
            <>
              {cancelButton}
              {rejectButton}
            </>
          );
          default: return cancelButton;
        }
      }

      // si es colaborador
      return cancelAcceptButtons;
    }

    return cancelAcceptButtons;
  }

  const getComponent = (field) => {
    const isFieldProtectedBySql = !isCreate && isFieldInProtectedList(field, recordProtectedFields);

    const getLabel = () => {
      if (field.disabled === "Y" || isFieldProtectedBySql) return `${field.name} (${getLocalizedString("protected")})`;
      if (field.disabled === "E") return `${field.name} (${getLocalizedString("protectedOnEdit")})`;
      return field.name;
    }

    const getIsDisabled = () => isFieldDisabled(field);

    const getListItems = () => {
      // Si el field tiene filterField, y este tiene valor, y los items tienen el dato para matchear, filtrar segun su valor. Sino, devovler todo el array.
      if (!field.filterField) return field.items;
      if (!formData[field.filterField]) return field.items;
      if (field.items.every((item) => !item.hasOwnProperty(field.filterField))) return field.items;
      return field.items.filter((item) => item[field.filterField] === formData[field.filterField]?.key);
    }

    if (field.hidden) return <></>;

    switch (field.component) {
      case componentTypes.text: return (
        <>
          <Tooltip title={field.tooltip || ""} arrow>
            <TextField
              required={field.required}
              disabled={getIsDisabled()}
              label={getLabel()}
              onChange={(e) => setFormValue(e.target.value, field.code)}
              value={formData[field.code]}
              inputProps={{ maxLength: field.size }}
              fullWidth
              margin={"none"}
            />
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </>
      );
      case componentTypes.list: return (
        <>
          <Tooltip title={field.tooltip || ""} arrow>
            <Autocomplete
              disabled={getIsDisabled()}
              style={{ width: '100%' }}
              options={getListItems()}
              value={formData[field.code]}
              onChange={(event, newUser) => {
                setFormValue(newUser, field.code);
              }}
              noOptionsText={''}
              getOptionLabel={(item) => item?.value || ''}
              renderInput={(params) => {
                return (
                  <TextField
                    required={field.required}
                    label={getLabel()}
                    {...params}
                  />
                )
              }}
            />
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </>
      );
      case componentTypes.listMultiple: return (
        <>
          <Tooltip title={field.tooltip || ""} arrow>
            <FormControl style={{ width: "100%" }}>
              <InputLabel required={field.required} id={`label-${[field.code]}`}>{getLabel()}</InputLabel>
              <Select
                multiple
                disabled={getIsDisabled()}
                value={formData[field.code]}
                labelId={`label-${[field.code]}`}
                id={`select-${[field.code]}`}
                onChange={(e) => setFormValue(e.target.value, field.code)}
                margin={"none"}
              >
                {
                  getListItems()?.map(item => (
                    <MenuItem
                      className={"whiteText"}
                      key={item.key}
                      value={item.key}>
                      {item.value}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </>
      );
      case componentTypes.multiline: return (
        <>
          <Tooltip title={field.tooltip || ""} arrow>
            <TextFieldMultiline
              style={{ marginTop: 10 }}
              disabled={getIsDisabled()}
              required={field.required}
              fullWidth
              label={getLabel()}
              rows={5}
              value={formData[field.code]}
              onChange={(e) => setFormValue(e.target.value, field.code)}
              inputProps={{ spellCheck: 'false' }}
            />
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </>
      );
      case componentTypes.dateWithPicker: return (
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
          <Tooltip title={field.tooltip || ""} arrow>
            <div>
              <DatePicker
                disabled={getIsDisabled()}
                required={field.required}
                label={getLabel()}
                value={formData[field.code]}
                onChange={(e) => setFormValue(e, field.code)}
                fullWidth
                cancelLabel={getLocalizedString("cancel")}
                okLabel={getLocalizedString("ok")}
                invalidDateMessage=""
                minDateMessage=""
                format="dd/MM/yyyy"
                margin="none"
              />
            </div>
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </MuiPickersUtilsProvider>
      );
      case componentTypes.timeWithPicker: return (
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
          <Tooltip title={field.tooltip || ""} arrow>
            <div>
              <TimePicker
                disabled={getIsDisabled()}
                required={field.required}
                label={getLabel()}
                value={formData[field.code]}
                onChange={(e) => setFormValue(e, field.code)}
                minutesStep={5}
                fullWidth
                clearable
                clearLabel={getLocalizedString("clear")}
                cancelLabel={getLocalizedString("cancel")}
                okLabel={getLocalizedString("ok")}
                invalidDateMessage=""
                minDateMessage=""
                format="HH:mm"
                margin="none"
                ampm={false}
              />
            </div>
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </MuiPickersUtilsProvider>
      );
      case componentTypes.periodWithPicker: return (
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
          <Tooltip title={field.tooltip || ""} arrow>
            <div>
              <DatePicker
                disabled={getIsDisabled()}
                required={field.required}
                label={getLabel()}
                value={formData[field.code]}
                onChange={(e) => setFormValue(e, field.code)}
                fullWidth
                cancelLabel={getLocalizedString("cancel")}
                okLabel={getLocalizedString("ok")}
                invalidDateMessage=""
                minDateMessage=""
                views={["month", "year"]}
                format="yyyy/MM"
                margin="none"
              />
            </div>
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </MuiPickersUtilsProvider>
      );
      case componentTypes.integer: return (
        <>
          <Tooltip title={field.tooltip || ""} arrow>
            <TextField
              required={field.required}
              disabled={getIsDisabled()}
              label={getLabel()}
              value={formData[field.code]}
              fullWidth
              margin={"none"}
              InputProps={{ inputComponent: CustomNumberFormat }}
              onChange={(e) => setFormValue(e.target.value.substring(0, field.size), field.code)}
              onBlur={(e) => e.target.value = e.target.value.substring(0, field.size)}
              inputProps={{ step: 1, maxLength: field.size, decimals: 'auto' }}
            />
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </>
      );
      case componentTypes.twoDecimals: return (
        <>
          <Tooltip title={field.tooltip || ""} arrow>
            <TextField
              required={field.required}
              disabled={getIsDisabled()}
              label={getLabel()}
              value={formData[field.code]}
              fullWidth
              margin={"none"}
              InputProps={{ inputComponent: CustomNumberFormat }}
              onChange={(e) => setFormValue(e.target.value.substring(0, field.size), field.code)}
              inputProps={{ min: "1", step: "0.01", decimals: 2 }}
            />
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </>
      );
      case componentTypes.threeDecimals: return (
        <>
          <Tooltip title={field.tooltip || ""} arrow>
            <TextField
              required={field.required}
              disabled={getIsDisabled()}
              label={getLabel()}
              value={formData[field.code]}
              fullWidth
              margin={"none"}
              InputProps={{ inputComponent: CustomNumberFormat }}
              onChange={(e) => setFormValue(e.target.value.substring(0, field.size), field.code)}
              inputProps={{ min: "1", step: "0.001", decimals: 3 }}
            />
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </>
      );
      case componentTypes.fourDecimals: return (
        <>
          <Tooltip title={field.tooltip || ""} arrow>
            <TextField
              required={field.required}
              disabled={getIsDisabled()}
              label={getLabel()}
              value={formData[field.code]}
              fullWidth
              margin={"none"}
              InputProps={{ inputComponent: CustomNumberFormat }}
              onChange={(e) => setFormValue(e.target.value.substring(0, field.size), field.code)}
              inputProps={{ min: "1", step: "0.0001", decimals: 4 }}
            />
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </>
      );
      case componentTypes.fiveDecimals: return (
        <>
          <Tooltip title={field.tooltip || ""} arrow>
            <TextField
              required={field.required}
              disabled={getIsDisabled()}
              label={getLabel()}
              value={formData[field.code]}
              fullWidth
              margin={"none"}
              InputProps={{ inputComponent: CustomNumberFormat }}
              onChange={(e) => setFormValue(e.target.value.substring(0, field.size), field.code)}
              inputProps={{ min: "1", step: "0.00001", decimals: 5 }}
            />
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </>
      );
      case componentTypes.checkbox: return (
        <>
          <div style={flexStyle} onClick={() => !getIsDisabled() && setFormValue(!Boolean(formData[field.code]), field.code)}>
            <Tooltip title={field.tooltip || ""} arrow>
              <div>
                <Typography className="whiteText" variant="subtitle1" style={{ display: "inline-block" }}>
                  {getLabel()}
                </Typography>
                {
                  field.required &&
                  <Typography variant="subtitle1" style={{ marginLeft: 5, display: "inline-block", color: "red" }}>
                    *
                  </Typography>
                }
              </div>
            </Tooltip>
            <Checkbox checked={formData[field.code]} />
          </div>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </>
      );
      case componentTypes.dateNoPicker: return (
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
          <Tooltip title={field.tooltip || ""} arrow>
            <div>
              <KeyboardDatePicker
                disabled={getIsDisabled()}
                required={field.required}
                label={getLabel()}
                value={formData[field.code]}
                onChange={(e) => setFormValue(e, field.code)}
                fullWidth
                cancelLabel={getLocalizedString("cancel")}
                okLabel={getLocalizedString("ok")}
                invalidDateMessage=""
                minDateMessage=""
                format="dd/MM/yyyy"
                margin="none"
              />
            </div>
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </MuiPickersUtilsProvider>
      );
      case componentTypes.timeNoPicker: return (
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
          <Tooltip title={field.tooltip || ""} arrow>
            <div>
              <KeyboardTimePicker
                disabled={getIsDisabled()}
                required={field.required}
                label={getLabel()}
                value={formData[field.code]}
                onChange={(e) => setFormValue(e, field.code)}
                invalidDateMessage=""
                minDateMessage=""
                minutesStep={5}
                fullWidth
                clearable
                clearLabel={getLocalizedString("clear")}
                cancelLabel={getLocalizedString("cancel")}
                okLabel={getLocalizedString("ok")}
                format="HH:mm"
                margin="none"
                ampm={false}
              />
            </div>
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </MuiPickersUtilsProvider>
      );
      case componentTypes.periodNoPicker: return (
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
          <Tooltip title={field.tooltip || ""} arrow>
            <div>
              <KeyboardDatePicker
                disabled={getIsDisabled()}
                required={field.required}
                label={getLabel()}
                value={formData[field.code]}
                onChange={(e) => setFormValue(e, field.code)}
                fullWidth
                cancelLabel={getLocalizedString("cancel")}
                okLabel={getLocalizedString("ok")}
                invalidDateMessage=""
                minDateMessage=""
                views={["month", "year"]}
                format="yyyy/MM"
                margin="none"
              />
            </div>
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </MuiPickersUtilsProvider>
      );
      case componentTypes.link:
      case componentTypes.fileLink:
        return (
          <>
            <Tooltip title={field.tooltip || ""} arrow>
              <TextField
                required={field.required}
                disabled={getIsDisabled()}
                label={getLabel()}
                onChange={(e) => setFormValue(e.target.value, field.code)}
                value={formData[field.code]}
                inputProps={{ maxLength: field.size }}
                fullWidth
                margin={"none"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => formData[field.code] && previewFromURL(formData[field.code])}
                      >
                        <LinkIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Tooltip>
            <HelperTextComponent>
              {field.comment}
            </HelperTextComponent>
          </>
        );
      case componentTypes.imageLink: return (
        <>
          <Tooltip title={field.tooltip || ""} arrow>
            <TextField
              required={field.required}
              disabled={getIsDisabled()}
              label={getLabel()}
              onChange={(e) => setFormValue(e.target.value, field.code)}
              value={formData[field.code]}
              inputProps={{ maxLength: field.size }}
              fullWidth
              margin={"none"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => formData[field.code] && previewFromURL(formData[field.code])}
                    >
                      <ImageIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </>
      );
      case componentTypes.geocoordinates: return (
        <>
          <Tooltip title={field.tooltip || ""} arrow>
            <TextField
              required={field.required}
              disabled={getIsDisabled()}
              label={getLabel()}
              onChange={(e) => setFormValue(e.target.value, field.code)}
              value={formData[field.code]}
              inputProps={{ maxLength: field.size }}
              fullWidth
              margin={"none"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => formData[field.code] && previewFromURL(`https://maps.google.com/?q=${formData[field.code]}`)}
                    >
                      <RoomIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Tooltip>
          <HelperTextComponent>
            {field.comment}
          </HelperTextComponent>
        </>
      );
      case componentTypes.imageInput: return (
        <>
          <Grid item>
            <input
              key={formData[field.code]}
              accept={acceptedFormatsImage.map(item => `.${item}`).join(',')}
              disabled={getIsDisabled()}
              type="file"
              id={field.code}
              style={{ display: "none" }}
              onChange={(e) => handleFileImageUpload(e, field.code)}
            />
            <div style={{ height: 48, display: 'flex', alignItems: 'center' }}>
              <label htmlFor={field.code}>
                <Tooltip title={field.tooltip || ""} arrow>
                  <Button
                    variant={"contained"}
                    disableRipple={true}
                    className="whiteText"
                    component={"span"}
                  >
                    {getLabel()}
                  </Button>
                </Tooltip>
              </label>
              {
                Boolean(formData[field.code]) &&
                <>
                  <img
                    style={{ height: 34, marginLeft: 15 }}
                    src={RESOURCES_ENDPOINT + formData[field.code]}
                    alt={field.name}
                  />
                  <Tooltip title={getLocalizedString("viewButton")}>
                    <IconButton onClick={() => previewFromURL(RESOURCES_ENDPOINT + formData[field.code])}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  {
                    !getIsDisabled() &&
                    <Tooltip title={getLocalizedString("delete")}>
                      <IconButton onClick={() =>
                        dispatch(openDialogAction({
                          title: getLocalizedString("atention"),
                          msg: getLocalizedString("deleteImageFieldQuestion", { name: field.name }),
                          onConfirm: () => setFormValue('', field.code),
                        }))}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  }
                </>
              }
            </div>
            <HelperTextComponent>
              {field.comment}
            </HelperTextComponent>
          </Grid>
        </>
      )
      case componentTypes.fileInput: return (
        <>
          <Grid item>
            <input
              disabled={getIsDisabled()}
              key={formData[field.code]}
              accept={acceptedFormatsFile.map(item => `.${item}`).join(',')}
              type="file"
              id={field.code}
              style={{ display: "none" }}
              onChange={(e) => handleFileImageUpload(e, field.code)}
            />
            <div style={{ height: 48, display: 'flex', alignItems: 'center' }}>
              <label htmlFor={field.code}>
                <Tooltip title={field.tooltip || ""} arrow>
                  <Button
                    variant={"contained"}
                    disableRipple={true}
                    className="whiteText"
                    component={"span"}
                  >
                    {getLabel()}
                  </Button>
                </Tooltip>
              </label>
              {
                Boolean(formData[field.code]) &&
                <>
                  <Tooltip title={getLocalizedString("download")}>
                    <IconButton onClick={() => downloadFromURL(RESOURCES_ENDPOINT + formData[field.code], field.name)}>
                      <GetAppIcon />
                    </IconButton>
                  </Tooltip>
                  {
                    !getIsDisabled() &&
                    <Tooltip title={getLocalizedString("delete")}>
                      <IconButton onClick={() =>
                        dispatch(openDialogAction({
                          title: getLocalizedString("atention"),
                          msg: getLocalizedString("deleteFileFieldQuestion", { name: field.name }),
                          onConfirm: () => setFormValue('', field.code),
                        }))}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  }
                </>
              }
            </div>
            <HelperTextComponent>
              {field.comment}
            </HelperTextComponent>
          </Grid>
        </>
      )
      default: return <></>;
    }
  }

  const fieldsKeyArrayWithBlankSpaces = useMemo(() => {
    const keysArray = Object.keys(fields);
    const newKeysArray = [];

    keysArray.forEach((key, index) => {
      newKeysArray.push(key);
      if (index !== keysArray.length - 1) { // si no es el ultimo
        const nextFieldItem = fields[keysArray[index + 1]]

        // si el actual es impar, ver si el proximo es impar, si lo es, agregar un item en el medio
        if (fields[key].order % 2 !== 0) {
          if (nextFieldItem.order % 2 !== 0) { // si el prodximo es impar
            newKeysArray.push(`fixOrder-${fields[key].order + 1}`); // como el proximo es tambien impar, agrego una key antes
          }
        } else {
          // si el actual es par, ver si el proximo es par, si lo es, agregar un item en el medio
          if (nextFieldItem.order % 2 === 0) { // si el prodximo es par
            newKeysArray.push(`fixOrder-${fields[key].order + 1}`); // como el proximo es tambien par, agrego una key antes
          }
        }
      }
    })
    return newKeysArray;
  }, [fields])

  if (isLoading) return null;

  return (
    <Dialog
      open={open}
      TransitionComponent={DialogTransition}
      PaperComponent={PaperDraggable}
      maxWidth={"lg"}
      fullWidth={true}
    >
      <div className={"dialog-container"}>
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          {isCreate ? getLocalizedString("createRecord") : getLocalizedString("editRecord")}
          <DialogTitleDivider />
        </DialogTitle>
        <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
        <DialogContent style={{ paddingTop: 0 }}>
          <Loader open={isLoadingFileUpload} />
          <Grid container item xs={12} style={{ maxHeight: 500 }}>
            {
              fieldsKeyArrayWithBlankSpaces.map((fieldKey) => {
                return (
                  <Grid key={fieldKey} item xs={12} sm={6} style={gridStyle}>
                    {
                      !fields[fieldKey]
                        ? <></>
                        : getComponent(fields[fieldKey])
                    }
                  </Grid>
                )
              })
            }
          </Grid>
        </DialogContent>
        <DialogActions>
          {getDialogActions()}
        </DialogActions>
      </div>
    </Dialog>
  );
}

export default CreateEditDialog;